import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import { ausgangAPI, kundenAPI } from '../lib/api';

const EMPTY = {
  kunden_id: '',
  rechnungsnummer: '',
  beleg_datum: new Date().toISOString().slice(0, 10),
  betrag_netto: '',
  ust_satz: '19',
  ust_betrag: '',
  betrag_brutto: '',
  buchungstext: '',
};

function recalcFromNetto(netto, satzPct) {
  const n = Number(netto);
  const s = Number(satzPct);
  if (isNaN(n) || isNaN(s)) return { ust: '', brutto: '' };
  const ust = +(n * s / 100).toFixed(2);
  const brutto = +(n + ust).toFixed(2);
  return { ust: ust.toFixed(2), brutto: brutto.toFixed(2) };
}

export default function AusgangFormModal({ open, onClose, slug, ausgang, prefill, onSaved }) {
  const isEdit = !!ausgang;
  const [form, setForm] = useState(EMPTY);
  const [kunden, setKunden] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    kundenAPI.list(slug, { limit: 500 }).then((r) => setKunden(r.data)).catch(() => {});
  }, [slug, open]);

  useEffect(() => {
    if (ausgang) {
      setForm({
        kunden_id: String(ausgang.kunden_id),
        rechnungsnummer: ausgang.rechnungsnummer || '',
        beleg_datum: ausgang.beleg_datum || EMPTY.beleg_datum,
        betrag_netto: String(ausgang.betrag_netto ?? ''),
        ust_satz: String(ausgang.ust_satz ?? '19').replace('.00', ''),
        ust_betrag: String(ausgang.ust_betrag ?? ''),
        betrag_brutto: String(ausgang.betrag_brutto ?? ''),
        buchungstext: ausgang.buchungstext || '',
      });
    } else if (prefill) {
      // PDF-First-Modus: aus AI-Extraktion vorbefüllt
      setForm({
        kunden_id: prefill.matched_kunde_id ? String(prefill.matched_kunde_id) : '',
        rechnungsnummer: prefill.rechnungsnummer || '',
        beleg_datum: prefill.beleg_datum || EMPTY.beleg_datum,
        betrag_netto: prefill.betrag_netto != null ? String(prefill.betrag_netto) : '',
        ust_satz: prefill.ust_satz != null ? String(Number(prefill.ust_satz)) : '19',
        ust_betrag: prefill.ust_betrag != null ? String(prefill.ust_betrag) : '',
        betrag_brutto: prefill.betrag_brutto != null ? String(prefill.betrag_brutto) : '',
        buchungstext: prefill.buchungstext || '',
      });
    } else {
      setForm(EMPTY);
    }
  }, [ausgang, prefill, open]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Auto-calc USt + Brutto when netto OR satz changes (only if user hasn't edited brutto/ust manually)
  const onNettoChange = (val) => {
    const { ust, brutto } = recalcFromNetto(val, form.ust_satz);
    setForm((f) => ({ ...f, betrag_netto: val, ust_betrag: ust, betrag_brutto: brutto }));
  };
  const onSatzChange = (val) => {
    const { ust, brutto } = recalcFromNetto(form.betrag_netto, val);
    setForm((f) => ({ ...f, ust_satz: val, ust_betrag: ust, betrag_brutto: brutto }));
  };

  const selectedKunde = useMemo(
    () => kunden.find((k) => String(k.id) === String(form.kunden_id)),
    [kunden, form.kunden_id]
  );

  // Auto-set ust_satz from kunde default when kunde changes (only on create)
  const onKundeChange = (val) => {
    setForm((f) => {
      const newF = { ...f, kunden_id: val };
      if (!isEdit) {
        const k = kunden.find((kk) => String(kk.id) === String(val));
        if (k && k.default_ust_satz != null) {
          const satz = String(Number(k.default_ust_satz));
          const { ust, brutto } = recalcFromNetto(f.betrag_netto, satz);
          newF.ust_satz = satz;
          newF.ust_betrag = ust;
          newF.betrag_brutto = brutto;
        }
      }
      return newF;
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.kunden_id) {
      toast.error('Kunde wählen');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        kunden_id: Number(form.kunden_id),
        rechnungsnummer: form.rechnungsnummer.trim(),
        beleg_datum: form.beleg_datum,
        betrag_netto: form.betrag_netto,
        ust_betrag: form.ust_betrag,
        betrag_brutto: form.betrag_brutto,
        ust_satz: form.ust_satz,
      };
      if (form.buchungstext) payload.buchungstext = form.buchungstext;

      if (isEdit) {
        await ausgangAPI.patch(slug, ausgang.id, payload);
        toast.success('Aktualisiert');
      } else {
        // PDF-Anhang aus upload-extract durchreichen
        if (prefill?.file_object_key) {
          payload.file_object_key = prefill.file_object_key;
          payload.file_hash_sha256 = prefill.file_hash_sha256;
          payload.page_count = prefill.page_count;
        }
        await ausgangAPI.create(slug, payload);
        toast.success(prefill ? 'Aus PDF angelegt' : 'Angelegt');
      }
      if (onSaved) await onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={onClose} className="modal-overlay">
        <Transition.Child as={Fragment}
          enter="fade-enter" enterFrom="fade-enter-from" enterTo="fade-enter-to"
          leave="fade-leave" leaveFrom="fade-leave-from" leaveTo="fade-leave-to">
          <div className="modal-backdrop" aria-hidden="true" />
        </Transition.Child>

        <div className="modal-positioner">
          <Transition.Child as={Fragment}
            enter="slide-enter" enterFrom="slide-enter-from" enterTo="slide-enter-to"
            leave="slide-leave" leaveFrom="slide-leave-from" leaveTo="slide-leave-to">
            <Dialog.Panel className="modal-panel" style={{ width: 540 }}>
              <form onSubmit={onSubmit}>
                <div className="modal-header">
                  <Dialog.Title className="modal-title">
                    {isEdit
                      ? `Ausgangsrechnung bearbeiten: ${ausgang.rechnungsnummer}`
                      : prefill
                        ? 'Neue Ausgangsrechnung (aus PDF)'
                        : 'Neue Ausgangsrechnung'}
                  </Dialog.Title>
                  <button type="button" className="btn-text" onClick={onClose}><X size={16} /></button>
                </div>

                {prefill && (
                  <div className="card" style={{
                    background: prefill.confidence >= 0.85 ? '#f0fdf4' : '#fffbeb',
                    borderLeft: `3px solid ${prefill.confidence >= 0.85 ? '#16a34a' : '#d97706'}`,
                    marginBottom: 12, padding: 10, fontSize: 12,
                  }}>
                    <div>
                      <b>KI-Vorschlag</b> ({prefill.ki_modell}) · Confidence{' '}
                      <b>{Math.round(prefill.confidence * 100)}%</b>
                      {prefill.kunde_name && (
                        <> · Erkannter Kunde: <i>{prefill.kunde_name}</i>
                          {!prefill.matched_kunde_id && (
                            <span style={{ color: '#991b1b' }}> — bitte unten auswählen / anlegen</span>
                          )}
                        </>
                      )}
                    </div>
                    {prefill.notes && <div className="muted" style={{ marginTop: 4 }}>{prefill.notes}</div>}
                  </div>
                )}

                <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr', marginTop: 12 }}>
                  <label style={{ gridColumn: '1 / span 2' }}>
                    Kunde *
                    <select
                      value={form.kunden_id}
                      onChange={(e) => onKundeChange(e.target.value)}
                      required
                    >
                      <option value="">— wählen —</option>
                      {kunden.map((k) => (
                        <option key={k.id} value={k.id}>
                          {k.name}{k.ust_id ? ` (${k.ust_id})` : ''}
                        </option>
                      ))}
                    </select>
                    {selectedKunde && (
                      <span className="muted small">
                        Erlös-Konto: {selectedKunde.default_konto || '— Fallback'}
                        {selectedKunde.debitor_konto ? ` · Debitor: ${selectedKunde.debitor_konto}` : ' · Sammelkonto Forderungen'}
                      </span>
                    )}
                  </label>

                  <label>
                    Rechnungs-Nr *
                    <input type="text" value={form.rechnungsnummer}
                      onChange={(e) => setField('rechnungsnummer', e.target.value)}
                      required maxLength={36} placeholder="z.B. AR-2026-001" />
                  </label>

                  <label>
                    Beleg-Datum *
                    <input type="date" value={form.beleg_datum}
                      onChange={(e) => setField('beleg_datum', e.target.value)} required />
                  </label>

                  <label>
                    Netto-Betrag *
                    <input type="number" step="0.01" min="0.01" value={form.betrag_netto}
                      onChange={(e) => onNettoChange(e.target.value)} required />
                  </label>

                  <label>
                    USt-Satz % *
                    <select value={form.ust_satz} onChange={(e) => onSatzChange(e.target.value)} required>
                      <option value="0">0%</option>
                      <option value="7">7%</option>
                      <option value="19">19%</option>
                    </select>
                  </label>

                  <label>
                    USt-Betrag *
                    <input type="number" step="0.01" min="0" value={form.ust_betrag}
                      onChange={(e) => setField('ust_betrag', e.target.value)} required />
                  </label>

                  <label>
                    Brutto-Betrag *
                    <input type="number" step="0.01" min="0.01" value={form.betrag_brutto}
                      onChange={(e) => setField('betrag_brutto', e.target.value)} required />
                  </label>

                  <label style={{ gridColumn: '1 / span 2' }}>
                    Buchungstext
                    <input type="text" value={form.buchungstext} maxLength={60}
                      onChange={(e) => setField('buchungstext', e.target.value)}
                      placeholder="default: AR {Nr} {Kundenname}" />
                  </label>
                </div>

                <div className="actions" style={{ borderTop: 'none', paddingTop: 12 }}>
                  <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Abbrechen</button>
                  <button type="submit" className="btn-primary" disabled={busy}>
                    {isEdit ? 'Speichern' : 'Anlegen'}
                  </button>
                </div>
              </form>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
